/*
 * GrowthBook reader — PLP Subcategory Carousel A/B test.
 *
 * Experiment: 'plp_subcategory_carousel_v1'
 * Feature flag: 'plp_subcategory_carousel_v1' (string; values: control|b)
 *
 * The row itself is CSS-driven (native overflow-x + scroll-snap in
 * src/scss/sections/_plp-subcategory-carousel.scss) — no Swiper, no init
 * step, no race with a deferred bundle.
 *
 * Depends on assets/growthbook-abtest.js (core):
 *   - checkHoldout() and window.ab_enabled managed by the core
 *   - the core does the single queue push and calls window.applyPlpSubcategoryCarousel
 *     from its applyFeatureFlags (initial + on each growthbookdata)
 *
 * Companion: assets/plp-subcategory-carousel-bootstrap.js — pre-first-paint
 * cache read; this file is authoritative and refreshes that cache.
 *
 * This file contributes:
 *   1. window.applyPlpSubcategoryCarousel — reader called by the core
 *   2. setupPlpSubcategoryCarousel() — click + impression tracking,
 *      runs once on DOM ready
 *   3. URL override (?plp_subcarr=b|control|off) for QA / preview sharing
 *
 * URL override:
 *   ?plp_subcarr=b        → force variant B, persists in sessionStorage
 *   ?plp_subcarr=control  → force control (carousel hidden), persists
 *   ?plp_subcarr=off      → clear the override, back to normal flow
 */
(() => {
  const EXPERIMENT_KEY = 'plp_subcategory_carousel_v1';
  const OVERRIDE_PARAM = 'plp_subcarr';
  const OVERRIDE_STORAGE_KEY = 'gb_plp_subcategory_carousel_override';
  const DECISION_KEY = 'plp_subcategory_carousel_v1_decision';
  const DECISION_TTL_MS = 24 * 60 * 60 * 1000;

  const getRoot = () => document.querySelector('[data-plp-subcategory-carousel]');

  const readOverride = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const fromUrl = urlParams.get(OVERRIDE_PARAM);
      if (fromUrl === 'off') {
        sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
        return null;
      }
      if (fromUrl !== null) {
        sessionStorage.setItem(OVERRIDE_STORAGE_KEY, fromUrl);
        return fromUrl;
      }
      return sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  let lastPersistedVariant = null;
  const persistVariantForTracking = (value) => {
    if (value === lastPersistedVariant) return;
    lastPersistedVariant = value;
    try {
      document.cookie = `gb_${EXPERIMENT_KEY}_variant=${value}; path=/; max-age=2592000; SameSite=Lax`;
    } catch (e) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'gb_variant_ready',
      experiment_id: EXPERIMENT_KEY,
      variation_id: value,
    });
  };

  // Attribute lives on <html> (not <body>) so the pre-first-paint bootstrap
  // in assets/plp-subcategory-carousel-bootstrap.js can set it before <body>
  // exists. Keep both in sync when touching this.
  const applyVariantToRoot = (variant) => {
    if (variant === 'b') {
      document.documentElement.dataset.gbPlpSubcategoryCarousel = 'b';
    } else {
      delete document.documentElement.dataset.gbPlpSubcategoryCarousel;
    }
  };

  const cacheDecision = (variant) => {
    const normalized = variant === 'b' ? 'b' : 'control';
    try {
      localStorage.setItem(
        DECISION_KEY,
        JSON.stringify({ variant: normalized, exp: Date.now() + DECISION_TTL_MS })
      );
    } catch (e) {
      /* Private mode / storage full: the next pageview flashes once. */
    }
  };

  // The core's queue push fires as soon as the SDK INSTANCE exists, which can
  // precede the features payload. In that window every `getFeatureValue` call
  // returns the default — so trusting it would (a) overwrite a correct cached
  // decision with `'control'` and (b) strip the attribute the bootstrap just
  // placed, re-introducing the flash this file is here to prevent. Mirrors the
  // readiness check in the plp_layout_v2 bootstrap (layout/theme.liquid).
  const isGrowthBookReady = (gb) => {
    if (!gb) return false;
    if (gb.ready === true) return true;
    try {
      const features = typeof gb.getFeatures === 'function' && gb.getFeatures();
      return !!features && Object.keys(features).length > 0;
    } catch (e) {
      return false;
    }
  };

  window.applyPlpSubcategoryCarousel = (gb) => {
    // Only touch anything when the section is on the page.
    if (!getRoot()) return;

    // URL override wins over GrowthBook (QA / shared preview). Don't cache —
    // the override is a QA signal, not an audience decision.
    const override = readOverride();
    if (override !== null) {
      window.ab_enabled = true;
      applyVariantToRoot(override);
      persistVariantForTracking(override === 'b' ? 'b' : 'control');
      return;
    }

    // Wait for the features payload before touching anything. The core will
    // call this again from the `growthbookdata` event once the payload lands;
    // meanwhile the bootstrap-applied attribute (from cache) stays untouched.
    if (!isGrowthBookReady(gb)) return;

    // Normal flow — respects the holdout (ab_enabled set by the core, which
    // only becomes authoritative once features are loaded).
    if (!window.ab_enabled) {
      applyVariantToRoot('control');
      persistVariantForTracking('holdout');
      cacheDecision('control');
      return;
    }

    const variant = gb.getFeatureValue(EXPERIMENT_KEY, 'control');
    applyVariantToRoot(variant);
    persistVariantForTracking(variant);
    cacheDecision(variant);
  };

  const track = (event, extra) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...extra });
  };

  const setupPlpSubcategoryCarousel = () => {
    const root = getRoot();
    if (!root) return;

    const collectionHandle = root.dataset.collectionHandle || null;
    const itemCount = root.querySelectorAll('[data-plp-subcategory-carousel-item]').length;

    // The row is CSS-driven (native overflow + scroll-snap), so there is no
    // library to initialise and no race to defend against. The impression
    // only needs the variant attribute on <html> — the pre-first-paint
    // bootstrap places it from cache before <body> parses; this
    // MutationObserver catches the later reader flip for cold-cache visits.
    let impressionFired = false;
    const maybeFireImpression = () => {
      if (document.documentElement.dataset.gbPlpSubcategoryCarousel !== 'b') return;
      if (impressionFired) return;
      impressionFired = true;
      track('plp_subcategory_carousel_impression', {
        experiment_id: EXPERIMENT_KEY,
        variation_id: 'b',
        collection_handle: collectionHandle,
        item_count: itemCount,
      });
    };

    maybeFireImpression();
    new MutationObserver(maybeFireImpression).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-gb-plp-subcategory-carousel'],
    });

    // Click delegation. `html.dataset.gbPlpSubcategoryCarousel` is authoritative
    // on the actual variant seen; if it's absent, the user saw control.
    root.addEventListener('click', (e) => {
      const item = e.target.closest('[data-plp-subcategory-carousel-item]');
      if (!item) return;
      const rawPosition = Number(item.dataset.position);
      track('plp_subcategory_carousel_click', {
        experiment_id: EXPERIMENT_KEY,
        variation_id: document.documentElement.dataset.gbPlpSubcategoryCarousel || 'control',
        collection_handle: collectionHandle,
        position: Number.isFinite(rawPosition) ? rawPosition : null,
        title: item.dataset.title || null,
        link: item.dataset.link || null,
      });
    });
  };

  // Setup runs once when the DOM is ready — independent of the queue.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPlpSubcategoryCarousel);
  } else {
    setupPlpSubcategoryCarousel();
  }

  // RACE: if GrowthBook was already initialized when the core pushed its
  // callback, that push ran BEFORE this file defined window.applyPlp… — and
  // if no other 'growthbookdata' fires, the flag never lands. Auto-apply here
  // if the SDK is already available; it's idempotent with the core's call.
  if (window._growthbook) window.applyPlpSubcategoryCarousel(window._growthbook);
})();
